import React from 'react'
import { Bullet, Bullets, Info, Page, Section, Step, Warning } from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const SafeguardingAndHealthSafety = () => (
  <Page
    title="Safeguarding and Health & Safety"
    intro="ePCRs capture safeguarding concerns and health and safety incidents separately from the main clinical entry, so they can be followed up by the right people after the record is submitted."
  >
    <Section title="Raising a safeguarding concern">
      <p>
        Safeguarding is answered on every full ePCR — the Review step will not
        let a full ePCR be submitted without a yes/no answer to{' '}
        <em>&quot;Are there any Safeguarding concerns?&quot;</em>. The
        questions appear as a Safeguarding tile in the Assess step, again in
        the Plan step if still unanswered, and as a dedicated Safeguarding tab
        on the mobile app.
      </p>
      <Bullets>
        <Bullet>
          When concerns are raised, the clinician records the concern details,
          who the concern was discussed with (Event Clinical Lead,
          Safeguarding Lead, GP, Duty Social Worker, Police, or a custom
          entry), whether a safeguarding referral has been made, and either
          the referral information or the reason a referral was not made.
        </Bullet>
        <Bullet>
          Critical Care records carry the same safeguarding questions as full
          ePCRs. Fast Track ePCRs do not include them — use a full ePCR
          whenever safeguarding could be a factor.
        </Bullet>
        <Bullet>
          Safeguarding incidents not tied to a patient record can be raised
          through governance incident reporting, which has an{' '}
          <em>&quot;Is this a safeguarding incident?&quot;</em> flag — see{' '}
          <DocLink
            docsPage
            url="/clinical-governance/incidents-and-safety-alerts"
            text="Incidents and Safety Alerts"
          />
          .
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Who can see and review safeguarding concerns?">
      <p>
        Safeguarding review is restricted to members of the{' '}
        <strong>Governance</strong> team. The server only serves the report
        view — where the concern and its review live — to Governance team
        members who have also passed the multi-factor authentication check.
        Other users cannot open someone else's record to see its safeguarding
        entry.
      </p>
      <Bullets>
        <Bullet>
          Governance team members see a red <strong>Safeguarding</strong>{' '}
          action chip on their home dashboard whenever submitted concerns are
          awaiting review. It opens Patient Report Forms pre-filtered to new
          safeguarding concerns.
        </Bullet>
        <Bullet>
          The Patient Report Forms search can also filter safeguarding
          concerns by <strong>New</strong> (no review recorded yet),{' '}
          <strong>Completed</strong> (reviewed), or <strong>Any</strong>.
        </Bullet>
        <Bullet>
          The governance KPI tracker reports a safeguarding review rate, so
          unreviewed concerns are visible at organisation level too.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="The review workflow">
      <Step number={1} title="Open the record">
        In the report view, records with concerns show the Safeguarding panel
        outlined in red, with the concern details, discussion, and referral
        answers.
      </Step>
      <Step number={2} title="Record the Manager / DSL review">
        Choose <strong>Actions → Review</strong> to record the review: notes
        or actions taken, who any referral was made to, and whether follow-up
        is required (with details and a follow-up date). The review stores the
        reviewer and the date it was actioned, and a hidden Clinical Advice
        Line entry notes that the concerns were reviewed. A review can be
        edited later from the same menu.
      </Step>
      <Step number={3} title="Close any follow-up">
        While a follow-up is outstanding, the reviewer who recorded it sees a{' '}
        <strong>Mark Follow Up Complete</strong> action on the record.
        Completing it is also noted on the Clinical Advice Line.
      </Step>
      <Info title="Concern states">
        A concern is treated as <strong>New</strong> until a review is
        recorded, and <strong>Completed</strong> once it has a reviewer —
        with an optional open follow-up until that is marked complete. There
        are no other statuses.
      </Info>
    </Section>

    <Section title="Health and safety incidents">
      <p>
        A health and safety concern can be recorded in the Incident step of
        both full and Fast Track ePCRs: the incident types involved, details,
        immediate action taken, whether the event organiser was notified, and
        any notes for the organiser.
      </p>
      <Bullets>
        <Bullet>
          Once the record is submitted, the concern is visible to the event
          organiser on their dashboard. A Governance user can hide it from
          (or re-show it to) the organiser from the report view.
        </Bullet>
        <Bullet>
          Governance team members see an <strong>H&amp;S Incidents</strong>{' '}
          chip on their home dashboard, and review a concern from the report
          view by recording manager notes — the review stores who reviewed it
          and when.
        </Bullet>
      </Bullets>
      <Warning title="Separate from governance incident reports">
        A health and safety concern on an ePCR is part of the patient record.
        If the incident needs formal investigation, raise it through
        governance incident reporting as well.
      </Warning>
    </Section>
  </Page>
)

export default SafeguardingAndHealthSafety
